import { create } from "zustand";
import axios from "axios";

export interface LocationData {
  address: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  coordinates: { lat: number; lon: number };
}

interface Place {
  display_name: string;
  lat: string;
  lon: string;
  address: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    region?: string;
    country?: string;
    postcode?: string;
  };
}

interface PlaceSearchStore {
  suggestions: LocationData[];
  selectedLocation: LocationData | null;
  currentLocation: { lat: number; lon: number } | null;
  fetchSuggestions: (query: string) => void;
  setSelectedLocation: (location: LocationData | null) => void;
  setCurrentLocation: (location: { lat: number; lon: number }) => void;
  reverseGeocode: (lat: number, lon: number) => Promise<LocationData | null>;
  clearLocationData: () => void;
}

export const usePlaceSearchStore = create<PlaceSearchStore>((set, get) => ({
  suggestions: [],
  selectedLocation: null,
  currentLocation: null,

  fetchSuggestions: async (query: string) => {
    if (query.length <= 2) {
      set({ suggestions: [] });
      return;
    }

    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          q: query,
          format: "json",
          addressdetails: 1,
          limit: 5,
        },
      });

      const suggestions: LocationData[] = response.data.map((place: Place) => {
        const address = place.address || {};
        const city = address.city || address.town || address.village || "";
        const state = address.state || address.region || "";
        const country = address.country || "";
        const postal_code = address.postcode || "";
        const fullLabel = place.display_name;

        return {
          address: fullLabel,
          city,
          state,
          country,
          postal_code: postal_code || undefined,
          coordinates: {
            lat: parseFloat(place.lat),
            lon: parseFloat(place.lon),
          },
        };
      });

      set({ suggestions });
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      set({ suggestions: [] });
    }
  },

  setSelectedLocation: (location) => {
    if (!location) {
      get().clearLocationData();
      return;
    }

    set({
      selectedLocation: location,
    });
  },

  setCurrentLocation: (location) => set({ currentLocation: location }),

  reverseGeocode: async (lat, lon) => {
    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/reverse`, {
        params: {
          lat,
          lon,
          format: "json",
          addressdetails: 1,
        },
      });

      const data = response.data;
      const address = data.address || {};

      const city = address.city || address.town || address.village || "";
      const state = address.state || address.region || "";
      const country = address.country || "";
      const postal_code = address.postcode || "";
      const fullLabel = data.display_name || "";

      const result: LocationData = {
        address: fullLabel,
        city,
        state,
        country,
        postal_code: postal_code || undefined,
        coordinates: { lat, lon },
      };

      return result;
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return null;
    }
  },

  clearLocationData: () =>
    set({
      suggestions: [],
      selectedLocation: null,
    }),
}));
